import { useCallback, useEffect, useState } from "react";
import { Download, Copy } from "lucide-react";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/i18n";
import { API } from "@/lib/api";
import { Card, PageTitle, Btn, Input, Select, Badge, Spinner, Empty, Modal, fmtMoney, fmtDate } from "@/components/ui-rp";
import { DisputeSection } from "./Dispute";

export const OrderDetail = ({ order, onClose, onChanged }) => {
  const { t, lang } = useI18n();
  const { client, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [refundOpen, setRefundOpen] = useState(false);
  const refundable = Math.round((order.amount - (order.refunded_amount || 0)) * 100) / 100;
  const [refundAmt, setRefundAmt] = useState(String(refundable));
  const [msg, setMsg] = useState("");
  const loadReceipts = useCallback(() => client.get(`/tenant/orders/${order.id}/receipts`).then(({ data }) => setReceipts(data)).catch(() => {}), [client, order.id]);
  useEffect(() => { loadReceipts(); }, [loadReceipts]);
  const act = async (path, body = {}) => {
    setBusy(true);
    try {
      await client.post(`/tenant/orders/${order.id}/${path}`, body);
      onChanged();
      onClose();
    } catch (e) {
      setMsg(e.response?.data?.detail?.message || t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => { await client.post(`/tenant/orders/${order.id}/resend-receipt`); setMsg(t("tx.queued")); loadReceipts(); };
  const preview = async (r) => { const { data } = await client.get(`/tenant/receipts/${r.id}/preview`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); };
  const copy = () => {
    navigator.clipboard?.writeText(order.checkout_url);
    setCopied(true);
  };
  return (
    <Modal title={`${t("tx.detail")} · ${order.order_number}`} onClose={onClose} testId="order-detail-modal">
      <div className="flex items-center justify-between">
        <p className="font-heading text-3xl font-extrabold" data-testid="order-detail-amount">{fmtMoney(order.amount)}</p>
        <Badge status={order.status} label={t(`status.${order.status}`)} testId="order-detail-status" />
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {[
          [t("tx.method"), order.method ? t(`method.${order.method}`) : "—"],
          [t("tx.ref"), order.provider_ref || "—"],
          [t("tx.customer"), order.customer?.email || "—"],
          [t("tx.date"), fmtDate(order.created_at, lang)],
        ].map(([k, v]) => (
          <div key={k}><dt className="text-muted-foreground">{k}</dt><dd className="font-medium break-all">{v}</dd></div>
        ))}
      </dl>
      {order.split && (
        <div className="rounded-xl bg-secondary p-4 text-sm space-y-1" data-testid="order-detail-split">
          <div className="flex justify-between"><span>{t("tx.amount")}</span><span>{fmtMoney(order.split.gross)}</span></div>
          <div className="flex justify-between text-muted-foreground"><span>{t("tx.fee")}</span><span>- {fmtMoney(order.split.rapidpay_fee)}</span></div>
          <div className="flex justify-between text-muted-foreground"><span>{t("tx.itbms")}</span><span>- {fmtMoney(order.split.itbms)}</span></div>
          <div className="flex justify-between font-bold border-t border-border pt-1"><span>{t("tx.net")}</span><span>{fmtMoney(order.split.merchant_net)}</span></div>
          {order.refunded_amount > 0 && <div className="flex justify-between text-sky-700 dark:text-sky-400" data-testid="order-detail-refunded"><span>{t("tx.refunded")}</span><span>- {fmtMoney(order.refunded_amount)}</span></div>}
        </div>
      )}
      {["paid", "refunded"].includes(order.status) && (
        <div data-testid="order-receipts">
          <div className="flex items-center justify-between mb-2"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("tx.receipts")}</p>
            {user.role !== "readonly" && <button type="button" onClick={resend} data-testid="order-resend-receipt" className="text-xs font-semibold underline">{t("tx.resend")}</button>}</div>
          {receipts.length === 0 ? <p className="text-xs text-muted-foreground">{t("tx.noReceipts")}</p> : (
            <ul className="space-y-1 text-xs">{receipts.map((r) => (
              <li key={r.id} className="flex justify-between gap-2" data-testid={`receipt-${r.id}`}><span className="truncate">{r.to} <span className="text-muted-foreground">· {r.kind.replace("_", " ")}</span></span>
                <button type="button" onClick={() => preview(r)} className="underline shrink-0" data-testid={`receipt-preview-${r.id}`}>{t("tx.preview")}</button></li>))}</ul>
          )}
          <p className="text-[11px] text-muted-foreground mt-1">{t("tx.mockMail")}</p>
        </div>
      )}
      {order.status === "chargeback" || order.dispute_status ? <DisputeSection order={order} onChanged={onChanged} /> : null}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("tx.timeline")}</p>
        <ul className="space-y-1 text-xs">
          {(order.events || []).map((e, i) => (
            <li key={i} className="flex justify-between gap-3"><span className="font-medium">{t(`status.${e.to}`)} <span className="text-muted-foreground">· {e.actor}</span></span><span className="text-muted-foreground font-mono">{fmtDate(e.at, lang)}</span></li>
          ))}
        </ul>
      </div>
      <div className="flex flex-wrap gap-2 pt-2">
        {["created", "pending"].includes(order.status) && order.checkout_url && (
          <Btn variant="outline" onClick={copy} testId="order-copy-link"><Copy className="w-4 h-4" />{copied ? t("tx.copied") : t("tx.copyLink")}</Btn>
        )}
        {["created", "pending"].includes(order.status) && order.customer?.email && user.role !== "readonly" && (
          <Btn variant="outline" busy={busy} onClick={async () => { setBusy(true); try { const { data } = await client.post(`/tenant/orders/${order.id}/remind`, {}); setMsg(t("tx.remindSent", { to: data.to })); } catch (e) { setMsg(e.response?.data?.detail?.message || t("common.error")); } finally { setBusy(false); } }} testId="order-remind-button">{t("tx.remind")}</Btn>
        )}
        {user.role === "admin" && ["created", "pending", "paid"].includes(order.status) && (
          <Btn variant="danger" busy={busy} onClick={() => act("void")} testId="order-void-button">{t("tx.void")}</Btn>
        )}
        {user.role === "admin" && order.status === "paid" && !refundOpen && (
          <Btn variant="outline" busy={busy} onClick={() => setRefundOpen(true)} testId="order-refund-button">{t("tx.refund")}</Btn>
        )}
      </div>
      {refundOpen && (
        <form onSubmit={(e) => { e.preventDefault(); act("refund", { amount: Number(refundAmt) }); }} className="rounded-xl border border-border p-3 space-y-2" data-testid="refund-form">
          <label className="block text-sm font-medium">{t("tx.refundAmount")} <span className="text-xs text-muted-foreground">({t("tx.refundable")}: {fmtMoney(refundable)})</span>
            <Input testId="refund-amount-input" type="number" step="0.01" min="0.01" max={refundable} required value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} className="mt-1" /></label>
          <div className="flex gap-2"><Btn type="submit" busy={busy} testId="refund-submit" className="h-10">{Number(refundAmt) < refundable ? t("tx.partial") : t("tx.refund")}</Btn><Btn type="button" variant="ghost" className="h-10" onClick={() => setRefundOpen(false)}>{t("common.cancel")}</Btn></div>
        </form>
      )}
      {msg && <p className={`text-sm ${/enviad|sent/i.test(msg) ? "text-[hsl(122,87%,30%)] dark:text-primary" : "text-destructive"}`} data-testid="order-detail-msg">{msg}</p>}
    </Modal>
  );
};

export const TransactionsPage = () => {
  const { t, lang } = useI18n();
  const { client } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    client.get("/tenant/orders", { params: { status: status || undefined, q: q || undefined, limit: 100 } }).then(({ data }) => setData(data)).catch(() => setData({ items: [], total: 0 }));
  }, [client, status, q]);
  useEffect(() => { load(); }, [load]);

  const openOrder = async (o) => {
    const { data } = await client.get(`/tenant/orders/${o.id}`);
    setSel(data);
  };
  const exportCsv = async () => {
    const { data } = await client.get("/tenant/orders/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = "transacciones.csv";
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="transactions-page">
      <PageTitle testId="transactions-title" action={<Btn variant="outline" onClick={exportCsv} testId="export-csv-button"><Download className="w-4 h-4" />{t("tx.export")}</Btn>}>{t("tx.title")}</PageTitle>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input testId="tx-search-input" placeholder={t("tx.search")} value={q} onChange={(e) => setQ(e.target.value)} />
        <Select testId="tx-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="sm:w-56">
          <option value="">{t("tx.all")}</option>
          {["created", "pending", "paid", "rejected", "expired", "voided", "refunded", "chargeback"].map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
        </Select>
      </div>
      {!data ? <Spinner testId="tx-loading" /> : data.items.length === 0 ? <Empty testId="tx-empty">{t("tx.empty")}</Empty> : (
        <>
          <div className="hidden md:block bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                {[t("tx.order"), t("tx.date"), t("tx.method"), t("tx.amount"), t("tx.net"), t("common.status")].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.items.map((o) => (
                  <tr key={o.id} onClick={() => openOrder(o)} data-testid={`tx-row-${o.order_number}`} className="border-b border-border last:border-0 hover:bg-secondary/60 cursor-pointer transition-colors">
                    <td className="px-5 py-3 font-mono font-medium">{o.order_number}</td>
                    <td className="px-5 py-3 text-muted-foreground">{fmtDate(o.created_at, lang)}</td>
                    <td className="px-5 py-3">{o.method ? t(`method.${o.method}`) : "—"}</td>
                    <td className="px-5 py-3 font-semibold">{fmtMoney(o.amount)}</td>
                    <td className="px-5 py-3 text-muted-foreground">{o.split ? fmtMoney(o.split.merchant_net) : "—"}</td>
                    <td className="px-5 py-3"><Badge status={o.status} label={t(`status.${o.status}`)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3">
            {data.items.map((o) => (
              <Card key={o.id} onClick={() => openOrder(o)} testId={`tx-card-${o.order_number}`} className="cursor-pointer">
                <div className="flex justify-between items-start gap-3">
                  <div><p className="font-mono font-semibold">{o.order_number}</p><p className="text-xs text-muted-foreground">{fmtDate(o.created_at, lang)} · {o.method ? t(`method.${o.method}`) : "—"}</p></div>
                  <div className="text-right"><p className="font-bold">{fmtMoney(o.amount)}</p><Badge status={o.status} label={t(`status.${o.status}`)} /></div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
      {sel && <OrderDetail order={sel} onClose={() => setSel(null)} onChanged={load} />}
    </div>
  );
};

export const VposPage = () => {
  const { t } = useI18n();
  const { client } = useAuth();
  const [form, setForm] = useState({ amount: "", description: "", customer_email: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!order || !["created", "pending"].includes(order.status)) return undefined;
    const id = setInterval(() => client.get(`/tenant/orders/${order.id}`).then(({ data }) => setOrder((o) => ({ ...o, ...data }))), 3000);
    return () => clearInterval(id);
  }, [order, client]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await client.post("/tenant/orders", { ...form, amount: Number(form.amount) }, { headers: { "Idempotency-Key": `vpos-${Date.now()}` } });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail?.message || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="vpos-page">
      <PageTitle testId="vpos-title">{t("vpos.title")}</PageTitle>
      {!order ? (
        <Card className="max-w-md">
          <form onSubmit={submit} className="space-y-4">
            <label className="block"><span className="block text-sm font-medium mb-1.5">{t("vpos.amount")}</span>
              <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">$</span>
                <Input testId="vpos-amount-input" type="number" step="0.01" min="0.01" inputMode="decimal" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-16 pl-10 text-3xl font-heading font-extrabold" /></div></label>
            <label className="block"><span className="block text-sm font-medium mb-1.5">{t("vpos.desc")}</span><Input testId="vpos-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label className="block"><span className="block text-sm font-medium mb-1.5">{t("vpos.email")}</span><Input testId="vpos-email-input" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></label>
            {error && <p className="text-sm text-destructive" data-testid="vpos-error">{error}</p>}
            <Btn type="submit" busy={busy} testId="vpos-charge-button" className="w-full h-12">{t("vpos.charge")}</Btn>
          </form>
        </Card>
      ) : (
        <Card className="max-w-md flex flex-col items-center text-center gap-4" testId="vpos-order-card">
          <p className="font-mono text-sm text-muted-foreground">{order.order_number}</p>
          <p className="font-heading text-4xl font-extrabold">{fmtMoney(order.amount)}</p>
          <Badge status={order.status} label={t(`status.${order.status}`)} testId="vpos-order-status" />
          {["created", "pending"].includes(order.status) ? (
            <>
              <img src={order.qr} alt="QR" className="w-56 h-56 rounded-xl bg-white p-2 border border-border" data-testid="vpos-qr" />
              <p className="text-sm text-muted-foreground">{t("vpos.scan")}</p>
              <p className="text-xs text-muted-foreground animate-pulse">{t("vpos.waiting")}</p>
              <a href={order.checkout_url} target="_blank" rel="noreferrer" className="text-sm font-semibold underline text-[hsl(122,87%,30%)] dark:text-primary" data-testid="vpos-open-checkout">{t("vpos.open")}</a>
            </>
          ) : (
            <p className={`font-semibold ${order.status === "paid" ? "text-[hsl(122,87%,30%)] dark:text-primary" : "text-destructive"}`} data-testid="vpos-result">{order.status === "paid" ? t("vpos.paidMsg") : t("vpos.failedMsg")}</p>
          )}
          <Btn variant="outline" onClick={() => { setOrder(null); setForm({ amount: "", description: "", customer_email: "" }); }} testId="vpos-new-button">{t("vpos.new")}</Btn>
        </Card>
      )}
    </div>
  );
};

export const LinksPage = () => {
  const { t } = useI18n();
  const { client, user } = useAuth();
  const [links, setLinks] = useState(null);
  const [show, setShow] = useState(false);
  const [qr, setQr] = useState(null);
  const [form, setForm] = useState({ title: "", amount: "", description: "", max_uses: "", expires_in_days: "" });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [sendTo, setSendTo] = useState("");
  const [sentTo, setSentTo] = useState("");
  const load = useCallback(() => client.get("/tenant/links").then(({ data }) => setLinks(data)).catch(() => setLinks([])), [client]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/tenant/links", { title: form.title, description: form.description, amount: form.amount ? Number(form.amount) : null, max_uses: form.max_uses ? Number(form.max_uses) : null, expires_in_days: form.expires_in_days ? Number(form.expires_in_days) : null });
      setShow(false);
      setForm({ title: "", amount: "", description: "", max_uses: "", expires_in_days: "" });
      load();
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (l) => { await client.patch(`/tenant/links/${l.link_id}`, { status: l.status === "active" ? "disabled" : "active" }); load(); };
  const copy = (l) => { navigator.clipboard?.writeText(l.url); setCopied(l.link_id); setTimeout(() => setCopied(""), 1500); };
  const dl = async (l, fmt) => {
    const { data } = await client.get(`/tenant/links/${l.link_id}/qr.${fmt}`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `qr-${l.link_id}.${fmt}`; a.click();
  };
  const canCreate = ["admin", "cashier"].includes(user.role);

  return (
    <div className="space-y-4" data-testid="links-page">
      <PageTitle testId="links-title" action={canCreate && <Btn onClick={() => setShow(true)} testId="create-link-button">+ {t("links.create")}</Btn>}>{t("links.title")}</PageTitle>
      {!links ? <Spinner /> : links.length === 0 ? <Empty testId="links-empty">{t("links.empty")}</Empty> : (
        <div className="grid gap-3 md:grid-cols-2">
          {links.map((l) => (
            <Card key={l.id} testId={`link-card-${l.link_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0"><p className="font-heading font-bold truncate">{l.title}</p><p className="text-xs text-muted-foreground font-mono truncate">{l.url.replace(/^https?:\/\//, "")}</p></div>
                <Badge status={l.status} label={t(`status.${l.status}`)} />
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="font-bold text-lg">{l.amount != null ? fmtMoney(l.amount) : t("links.open")}</span>
                <span className="text-xs text-muted-foreground">{l.paid_count}{l.max_uses ? `/${l.max_uses}` : ""} {t("links.uses")}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Btn variant="outline" className="h-10 px-4" onClick={() => copy(l)} testId={`link-copy-${l.link_id}`}>{copied === l.link_id ? t("tx.copied") : t("links.copy")}</Btn>
                <Btn variant="outline" className="h-10 px-4" onClick={() => setQr(l)} testId={`link-qr-${l.link_id}`}>{t("links.qr")}</Btn>
                {user.role === "admin" && <Btn variant="ghost" className="h-10 px-4" onClick={() => toggle(l)} testId={`link-toggle-${l.link_id}`}>{l.status === "active" ? t("links.disable") : t("links.enable")}</Btn>}
              </div>
            </Card>
          ))}
        </div>
      )}
      {show && (
        <Modal title={t("links.create")} onClose={() => setShow(false)} testId="create-link-modal">
          <form onSubmit={submit} className="space-y-3">
            {[["title", t("links.name"), "text", true], ["amount", t("links.amount"), "number"], ["description", t("links.desc"), "text"], ["max_uses", t("links.maxUses"), "number"], ["expires_in_days", t("links.expires"), "number"]].map(([k, label, type, req]) => (
              <label key={k} className="block"><span className="block text-sm font-medium mb-1.5">{label}</span>
                <Input testId={`create-link-${k}-input`} type={type} step={type === "number" ? "0.01" : undefined} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></label>
            ))}
            <Btn type="submit" busy={busy} testId="create-link-submit" className="w-full h-12">{t("common.create")}</Btn>
          </form>
        </Modal>
      )}
      {qr && (
        <Modal title={qr.title} onClose={() => setQr(null)} testId="link-qr-modal">
          <div className="flex flex-col items-center gap-3">
            <img src={`${API}/tenant/links/${qr.link_id}/qr.png`} alt="QR" className="hidden" />
            <QrImg link={qr} client={client} />
            <p className="text-sm text-muted-foreground text-center">{t("links.share")}</p>
            <p className="text-xs font-mono break-all text-center">{qr.url}</p>
            <div className="flex gap-2"><Btn variant="outline" onClick={() => dl(qr, "png")} testId="qr-download-png">{t("links.download")}</Btn><Btn variant="outline" onClick={() => dl(qr, "svg")} testId="qr-download-svg">{t("links.downloadSvg")}</Btn></div>
            <form onSubmit={async (e) => { e.preventDefault(); await client.post(`/tenant/links/${qr.link_id}/send`, { email: sendTo }); setSentTo(sendTo); setSendTo(""); }} className="w-full flex gap-2 pt-2 border-t border-border">
              <Input testId="link-send-email" type="email" required placeholder={t("tx.sendLinkTo")} value={sendTo} onChange={(e) => setSendTo(e.target.value)} className="h-11" />
              <Btn type="submit" className="h-11 shrink-0" testId="link-send-submit">{t("tx.sendLink")}</Btn>
            </form>
            {sentTo && <p className="text-xs text-[hsl(122,87%,30%)] dark:text-primary" data-testid="link-sent-msg">{t("tx.sent")} · {sentTo}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
};

const QrImg = ({ link, client }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    client.get(`/tenant/links/${link.link_id}/qr.png`, { responseType: "blob" }).then(({ data }) => setSrc(URL.createObjectURL(data)));
  }, [link, client]);
  return src ? <img src={src} alt="QR" className="w-56 h-56 rounded-xl bg-white p-2 border border-border" data-testid="link-qr-image" /> : <Spinner />;
};
